import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Crop,
  RotateCw,
  RotateCcw,
  Undo2,
  Redo2,
  Save,
  Copy,
  SlidersHorizontal,
  Loader2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import api from '@/services/api';

interface ReceiptImageEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageSrc: string;
  receiptId: string;
  onSaved?: () => void;
}

interface EditorState {
  brightness: number;
  contrast: number;
  saturation: number;
  rotation: number;
  freeRotation: number;
  baseImageDataUrl: string;
}

interface CropRegion {
  x: number;
  y: number;
  w: number;
  h: number;
}

const MAX_HISTORY = 20;

function buildCssFilter(state: EditorState): string {
  const b = 1 + state.brightness / 100;
  const c = 1 + state.contrast / 100;
  const s = 1 + state.saturation / 100;
  return `brightness(${b}) contrast(${c}) saturate(${s})`;
}

function imageToDataUrl(img: HTMLImageElement): string {
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (ctx) ctx.drawImage(img, 0, 0);
  return canvas.toDataURL('image/png');
}

export default function ReceiptImageEditor({
  open,
  onOpenChange,
  imageSrc,
  receiptId,
  onSaved,
}: ReceiptImageEditorProps) {
  const { toast } = useToast();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const baseImageRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const displayScaleRef = useRef(1);

  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tool, setTool] = useState<'adjust' | 'crop' | 'rotate'>('adjust');

  const [state, setState] = useState<EditorState>({
    brightness: 0,
    contrast: 0,
    saturation: 0,
    rotation: 0,
    freeRotation: 0,
    baseImageDataUrl: '',
  });

  const [history, setHistory] = useState<EditorState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const [cropMode, setCropMode] = useState(false);
  const [cropRegion, setCropRegion] = useState<CropRegion | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const cropStartRef = useRef<{ x: number; y: number } | null>(null);

  // Load image via fetch+blob to avoid tainted canvas
  useEffect(() => {
    if (!open) return;
    setLoaded(false);

    (async () => {
      try {
        const response = await fetch(imageSrc, { credentials: 'include' });
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        const img = new Image();
        img.onload = () => {
          baseImageRef.current = img;
          const dataUrl = imageToDataUrl(img);
          const initialState: EditorState = {
            brightness: 0,
            contrast: 0,
            saturation: 0,
            rotation: 0,
            freeRotation: 0,
            baseImageDataUrl: dataUrl,
          };
          setState(initialState);
          setHistory([initialState]);
          setHistoryIndex(0);
          setLoaded(true);
          setCropRegion(null);
          setCropMode(false);
          setTool('adjust');
          URL.revokeObjectURL(objectUrl);
        };
        img.onerror = () => {
          toast({ title: 'Failed to load image', variant: 'destructive' });
          URL.revokeObjectURL(objectUrl);
        };
        img.src = objectUrl;
      } catch {
        toast({ title: 'Failed to load image', variant: 'destructive' });
      }
    })();
  }, [open, imageSrc, toast]);

  // Render canvas
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = baseImageRef.current;
    if (!canvas || !img || !loaded) return;

    const totalRotation = state.rotation + state.freeRotation;
    const radians = (totalRotation * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(radians));
    const absSin = Math.abs(Math.sin(radians));

    const imgW = img.width;
    const imgH = img.height;
    const rotW = imgW * absCos + imgH * absSin;
    const rotH = imgW * absSin + imgH * absCos;

    const container = containerRef.current;
    const maxW = container ? container.clientWidth - 16 : 800;
    const maxH = container ? container.clientHeight - 16 : 500;
    const scaleFactor = Math.min(1, maxW / rotW, maxH / rotH);
    displayScaleRef.current = scaleFactor;

    canvas.width = Math.round(rotW * scaleFactor);
    canvas.height = Math.round(rotH * scaleFactor);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(radians);
    ctx.filter = buildCssFilter(state);
    ctx.drawImage(
      img,
      (-imgW * scaleFactor) / 2,
      (-imgH * scaleFactor) / 2,
      imgW * scaleFactor,
      imgH * scaleFactor,
    );
    ctx.restore();

    // Crop overlay
    if (cropMode && cropRegion) {
      ctx.save();
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.clearRect(cropRegion.x, cropRegion.y, cropRegion.w, cropRegion.h);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(cropRegion.x, cropRegion.y, cropRegion.w, cropRegion.h);
      ctx.restore();
    }
  }, [state, loaded, cropMode, cropRegion]);

  useEffect(() => {
    renderCanvas();
  }, [renderCanvas]);

  // Push state to history
  const pushHistory = useCallback(
    (newState: EditorState) => {
      setHistory((prev) => {
        const trimmed = prev.slice(0, historyIndex + 1);
        const next = [...trimmed, newState];
        if (next.length > MAX_HISTORY) next.shift();
        return next;
      });
      setHistoryIndex((prev) => Math.min(prev + 1, MAX_HISTORY - 1));
      setState(newState);
    },
    [historyIndex],
  );

  // Undo/Redo
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const handleUndo = useCallback(() => {
    if (!canUndo) return;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    const prevState = history[newIndex];
    setState(prevState);
    if (prevState.baseImageDataUrl !== state.baseImageDataUrl) {
      const img = new Image();
      img.onload = () => {
        baseImageRef.current = img;
      };
      img.src = prevState.baseImageDataUrl;
    }
  }, [canUndo, historyIndex, history, state.baseImageDataUrl]);

  const handleRedo = useCallback(() => {
    if (!canRedo) return;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    const nextState = history[newIndex];
    setState(nextState);
    if (nextState.baseImageDataUrl !== state.baseImageDataUrl) {
      const img = new Image();
      img.onload = () => {
        baseImageRef.current = img;
      };
      img.src = nextState.baseImageDataUrl;
    }
  }, [canRedo, historyIndex, history, state.baseImageDataUrl]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, handleUndo, handleRedo]);

  // Adjustments
  const handleAdjust = (key: 'brightness' | 'contrast' | 'saturation', value: number) => {
    setState((prev) => ({ ...prev, [key]: value }));
  };

  const commitAdjust = () => {
    pushHistory({ ...state });
  };

  // Rotation
  const handleRotate90 = (direction: 1 | -1) => {
    pushHistory({ ...state, rotation: state.rotation + direction * 90 });
  };

  const handleFreeRotation = (value: number) => {
    setState((prev) => ({ ...prev, freeRotation: value }));
  };

  const commitFreeRotation = () => {
    pushHistory({ ...state });
  };

  // Reset
  const handleReset = () => {
    if (history.length === 0) return;
    const initial = history[0];
    const img = new Image();
    img.onload = () => {
      baseImageRef.current = img;
      pushHistory({ ...initial });
    };
    img.src = initial.baseImageDataUrl;
  };

  // Crop handlers
  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleCropMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!cropMode) return;
    const coords = getCanvasCoords(e);
    cropStartRef.current = coords;
    setIsCropping(true);
    setCropRegion({ x: coords.x, y: coords.y, w: 0, h: 0 });
  };

  const handleCropMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!cropMode || !isCropping || !cropStartRef.current) return;
    const coords = getCanvasCoords(e);
    const start = cropStartRef.current;
    setCropRegion({
      x: Math.min(start.x, coords.x),
      y: Math.min(start.y, coords.y),
      w: Math.abs(coords.x - start.x),
      h: Math.abs(coords.y - start.y),
    });
  };

  const handleCropMouseUp = () => {
    setIsCropping(false);
  };

  const applyCrop = () => {
    if (!cropRegion || !baseImageRef.current || cropRegion.w < 10 || cropRegion.h < 10) return;

    const scale = displayScaleRef.current;
    const fullCrop = {
      x: cropRegion.x / scale,
      y: cropRegion.y / scale,
      w: cropRegion.w / scale,
      h: cropRegion.h / scale,
    };

    // Render at full resolution with current transforms
    const img = baseImageRef.current;
    const totalRotation = state.rotation + state.freeRotation;
    const radians = (totalRotation * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(radians));
    const absSin = Math.abs(Math.sin(radians));
    const fullW = Math.round(img.width * absCos + img.height * absSin);
    const fullH = Math.round(img.width * absSin + img.height * absCos);

    const fullCanvas = document.createElement('canvas');
    fullCanvas.width = fullW;
    fullCanvas.height = fullH;
    const fullCtx = fullCanvas.getContext('2d');
    if (!fullCtx) return;

    fullCtx.translate(fullW / 2, fullH / 2);
    fullCtx.rotate(radians);
    fullCtx.filter = buildCssFilter(state);
    fullCtx.drawImage(img, -img.width / 2, -img.height / 2);

    // Extract crop at full resolution
    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = Math.round(fullCrop.w);
    cropCanvas.height = Math.round(fullCrop.h);
    const cropCtx = cropCanvas.getContext('2d');
    if (!cropCtx) return;

    cropCtx.drawImage(
      fullCanvas,
      Math.round(fullCrop.x),
      Math.round(fullCrop.y),
      Math.round(fullCrop.w),
      Math.round(fullCrop.h),
      0,
      0,
      Math.round(fullCrop.w),
      Math.round(fullCrop.h),
    );

    const dataUrl = cropCanvas.toDataURL('image/png');
    const newImg = new Image();
    newImg.onload = () => {
      baseImageRef.current = newImg;
      pushHistory({
        brightness: 0,
        contrast: 0,
        saturation: 0,
        rotation: 0,
        freeRotation: 0,
        baseImageDataUrl: dataUrl,
      });
      setCropRegion(null);
      setCropMode(false);
      setTool('adjust');
    };
    newImg.src = dataUrl;
  };

  // Save
  const handleSave = async (asCopy: boolean) => {
    const img = baseImageRef.current;
    if (!img) return;

    const totalRotation = state.rotation + state.freeRotation;
    const radians = (totalRotation * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(radians));
    const absSin = Math.abs(Math.sin(radians));
    const outW = Math.round(img.width * absCos + img.height * absSin);
    const outH = Math.round(img.width * absSin + img.height * absCos);

    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = outW;
    outputCanvas.height = outH;
    const outCtx = outputCanvas.getContext('2d');
    if (!outCtx) return;

    outCtx.translate(outW / 2, outH / 2);
    outCtx.rotate(radians);
    outCtx.filter = buildCssFilter(state);
    outCtx.drawImage(img, -img.width / 2, -img.height / 2);

    setSaving(true);
    try {
      const blob = await new Promise<Blob | null>((resolve) => {
        outputCanvas.toBlob(resolve, 'image/webp', 0.85);
      });
      if (!blob) throw new Error('Failed to export image');

      const formData = new FormData();
      formData.append('file', blob, 'edited-receipt.webp');

      if (asCopy) {
        await api.post('/receipts', formData, { timeout: 60000 });
        toast({ title: 'Saved as new receipt' });
      } else {
        formData.append('replaceReceiptId', receiptId);
        await api.post('/receipts', formData, { timeout: 60000 });
        toast({ title: 'Edited receipt saved' });
      }

      onSaved?.();
      onOpenChange(false);
    } catch {
      toast({ title: 'Failed to save image', variant: 'destructive' });
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[90vh] max-w-5xl flex-col p-0 sm:max-w-5xl"
        data-testid="receipt-image-editor"
      >
        <DialogHeader className="px-4 pb-2 pt-4">
          <DialogTitle>Edit Receipt Image</DialogTitle>
          <DialogDescription>Adjust, crop, or rotate the receipt image</DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          {/* Canvas area */}
          <div
            ref={containerRef}
            className="flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-muted/30 p-2"
          >
            {loaded ? (
              <canvas
                ref={canvasRef}
                className={cn('max-h-full max-w-full', cropMode && 'cursor-crosshair')}
                onMouseDown={handleCropMouseDown}
                onMouseMove={handleCropMouseMove}
                onMouseUp={handleCropMouseUp}
                onMouseLeave={handleCropMouseUp}
                data-testid="editor-canvas"
              />
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading image...
              </div>
            )}
          </div>

          {/* Toolbar sidebar */}
          <div className="w-full space-y-4 overflow-y-auto border-t bg-card p-4 md:w-72 md:border-l md:border-t-0">
            <Tabs
              value={tool}
              onValueChange={(v) => {
                setTool(v as typeof tool);
                setCropMode(v === 'crop');
              }}
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="adjust" className="min-h-[44px]" data-testid="tool-adjust">
                  <SlidersHorizontal className="h-4 w-4" />
                </TabsTrigger>
                <TabsTrigger value="crop" className="min-h-[44px]" data-testid="tool-crop">
                  <Crop className="h-4 w-4" />
                </TabsTrigger>
                <TabsTrigger value="rotate" className="min-h-[44px]" data-testid="tool-rotate">
                  <RotateCw className="h-4 w-4" />
                </TabsTrigger>
              </TabsList>

              {/* Adjustments */}
              <TabsContent value="adjust" className="mt-4 space-y-4">
                {(
                  [
                    { key: 'brightness', label: 'Brightness' },
                    { key: 'contrast', label: 'Contrast' },
                    { key: 'saturation', label: 'Saturation' },
                  ] as const
                ).map(({ key, label }) => (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">{label}</Label>
                      <span className="w-8 text-right text-xs text-muted-foreground">
                        {state[key]}
                      </span>
                    </div>
                    <Slider
                      min={-100}
                      max={100}
                      step={1}
                      value={[state[key]]}
                      onValueChange={([v]) => handleAdjust(key, v)}
                      onValueCommit={() => commitAdjust()}
                      data-testid={`slider-${key}`}
                    />
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full min-h-[44px]"
                  onClick={() => {
                    pushHistory({
                      ...state,
                      brightness: 0,
                      contrast: 0,
                      saturation: 0,
                    });
                  }}
                  data-testid="adjust-reset"
                >
                  Reset Adjustments
                </Button>
              </TabsContent>

              {/* Crop */}
              <TabsContent value="crop" className="mt-4 space-y-3">
                <p className="text-xs text-muted-foreground">
                  Click and drag on the image to select the area to keep.
                </p>
                {cropRegion && cropRegion.w > 10 && cropRegion.h > 10 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      Selection: {Math.round(cropRegion.w)} × {Math.round(cropRegion.h)}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="min-h-[44px] flex-1"
                        onClick={applyCrop}
                        data-testid="apply-crop"
                      >
                        Apply Crop
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-h-[44px]"
                        onClick={() => setCropRegion(null)}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Rotate */}
              <TabsContent value="rotate" className="mt-4 space-y-4">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="min-h-[44px] flex-1"
                    onClick={() => handleRotate90(-1)}
                    data-testid="rotate-left"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" /> 90° Left
                  </Button>
                  <Button
                    variant="outline"
                    className="min-h-[44px] flex-1"
                    onClick={() => handleRotate90(1)}
                    data-testid="rotate-right"
                  >
                    <RotateCw className="mr-2 h-4 w-4" /> 90° Right
                  </Button>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Free Rotation</Label>
                    <span className="text-xs text-muted-foreground">{state.freeRotation}°</span>
                  </div>
                  <Slider
                    min={-45}
                    max={45}
                    step={0.5}
                    value={[state.freeRotation]}
                    onValueChange={([v]) => handleFreeRotation(v)}
                    onValueCommit={() => commitFreeRotation()}
                    data-testid="slider-rotation"
                  />
                </div>
              </TabsContent>
            </Tabs>

            <Separator />

            {/* Undo/Redo */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px] flex-1"
                disabled={!canUndo}
                onClick={handleUndo}
                data-testid="undo"
              >
                <Undo2 className="mr-1 h-4 w-4" /> Undo
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px] flex-1"
                disabled={!canRedo}
                onClick={handleRedo}
                data-testid="redo"
              >
                <Redo2 className="mr-1 h-4 w-4" /> Redo
              </Button>
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="w-full min-h-[44px]"
              onClick={handleReset}
              data-testid="editor-reset"
            >
              Reset All
            </Button>

            <Separator />

            {/* Save actions */}
            <div className="space-y-2">
              <Button
                className="w-full min-h-[44px]"
                onClick={() => handleSave(false)}
                disabled={saving}
                data-testid="save-replace"
              >
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outline"
                className="w-full min-h-[44px]"
                onClick={() => handleSave(true)}
                disabled={saving}
                data-testid="save-copy"
              >
                <Copy className="mr-2 h-4 w-4" /> Save as Copy
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
